// ---------------------------------------------------------------------------
// Video progress: saves where the viewer is in a video (flipclass_api:view), so the next visit resumes there and the
// course pages show how far they got. Works on a small player adapter, so the logic runs (and is tested) without
// Vimeo — see video-progress.test.mjs.
//
// Saving never stops: a failed request is retried with the next save, and only the newest position is ever sent.
// It saves every SAVE_EVERY_MS while playing, at once on pause / seek / end, and on leaving the page (keepalive).
// ---------------------------------------------------------------------------

export const SAVE_EVERY_MS = 10000

// A player adapter: on(event, fn(seconds, duration)) for play / timeupdate / pause / seeked / ended, and
// seek(seconds) -> Promise.
export function vimeoAdapter(player) {
  return {
    on(event, fn) { player.on(event, (e) => fn(e.seconds, e.duration)) },
    seek(seconds) { return player.setCurrentTime(seconds) },
  }
}

export function html5Adapter(video) {
  return {
    on(event, fn) { video.addEventListener(event, () => fn(video.currentTime, video.duration)) },
    seek(seconds) {
      video.currentTime = seconds
      return Promise.resolve()
    },
  }
}

// send(url, {position, duration}, keepalive) -> Promise that rejects when the save failed.
// Returns {leave}: call it when the page is hidden or unloaded.
export function trackProgress(player, { url, resumeAt = 0, send, now = Date.now }) {
  let pending = null // the newest {position, duration} not yet saved
  let urgent = false // save pending as soon as the request in flight is done
  let inFlight = false
  let lastSent = -Infinity
  // Until the resume seek is done the player reports the start of the video, which must not overwrite the position.
  let resuming = resumeAt > 0

  function flush(keepalive = false) {
    if (!pending || (inFlight && !keepalive)) return
    const body = pending
    pending = null
    urgent = false
    lastSent = now()
    if (keepalive) {
      send(url, body, true).catch(() => {})
      return
    }
    inFlight = true
    send(url, body, false)
      .catch(() => { if (!pending) pending = body })
      .finally(() => {
        inFlight = false
        if (urgent) flush()
      })
  }

  function update(seconds, duration, immediately = false) {
    if (resuming || !(duration > 0) || !Number.isFinite(seconds)) return
    pending = { position: Math.min(Math.max(seconds, 0), duration), duration }
    if (immediately) urgent = true
    if (urgent || now() - lastSent >= SAVE_EVERY_MS) flush()
  }

  player.on('play', () => {
    if (!resuming) return
    player.seek(resumeAt).catch(() => {}).finally(() => { resuming = false })
  })
  player.on('timeupdate', (seconds, duration) => update(seconds, duration))
  player.on('pause', (seconds, duration) => update(seconds, duration, true))
  player.on('seeked', (seconds, duration) => update(seconds, duration, true))
  player.on('ended', (seconds, duration) => update(duration, duration, true))

  return { leave: () => flush(true) }
}
